/**
 * Review System (Jonathan 2026-05-21) — the player rates player-created
 * levels (Fun 1–5 stars, Difficulty 1–5 skulls) at the end of a session, and
 * player profiles / gallery levels carry aggregate star ratings "from other
 * players".
 *
 * Two halves:
 *  - Ratings the player *gives* are persisted in localStorage (keyed by level
 *    key) so a re-played level shows the prior rating and the analyst export
 *    can reconstruct them.
 *  - Aggregates shown on other authors' profiles + gallery entries are
 *    *simulated* deterministically from the author/level seed (no backend,
 *    matching the faked-multiplayer posture of the rest of the UGC layer).
 */

import type { LevelRating } from "../types";

import { makeSeededRng } from "./seededRng";

const STORAGE_KEY = "roamling.ratings";

export interface RatingAggregate {
  /** Mean fun rating (1–5, one decimal). */
  fun: number;
  /** Mean difficulty rating (1–5, one decimal). */
  difficulty: number;
  /** Plausible number of ratings behind the average. */
  count: number;
}

/** Stable level key from an author/level seed. */
export function levelRatingKey(authorSeed: number): string {
  return `level:${authorSeed >>> 0}`;
}

function readAll(): Record<string, LevelRating> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, LevelRating>;
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function writeAll(map: Record<string, LevelRating>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    return;
  }
}

function clampStar(n: number): number {
  return Math.max(1, Math.min(5, Math.round(n)));
}

export class RatingService {
  /** The rating the player previously gave this level, if any. */
  static getGiven(levelKey: string): LevelRating | null {
    const all = readAll();
    const hit = all[levelKey];
    if (!hit) return null;
    return { fun: clampStar(hit.fun), difficulty: clampStar(hit.difficulty) };
  }

  /** Persist the player's rating of a level. */
  static submit(levelKey: string, rating: LevelRating): void {
    const all = readAll();
    all[levelKey] = { fun: clampStar(rating.fun), difficulty: clampStar(rating.difficulty) };
    writeAll(all);
  }

  /**
   * Deterministic simulated "other players" aggregate for an author/level
   * seed. Same seed → same numbers. Biased toward the upper half (3.2–4.8)
   * so the simulated community reads as broadly positive without being flat.
   */
  static simulatedAggregate(seed: number): RatingAggregate {
    const rng = makeSeededRng((seed >>> 0) ^ 0x9e3779b9);
    const fun = 3.2 + rng.next() * 1.6; // 3.2 – 4.8
    const difficulty = 1.8 + rng.next() * 2.6; // 1.8 – 4.4
    const count = rng.intRange(4, 90);
    return {
      fun: Math.round(fun * 10) / 10,
      difficulty: Math.round(difficulty * 10) / 10,
      count,
    };
  }
}
